'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNavigation,
  CarouselControls,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import type { VideoItem, VideoConsentLabels } from '@/app/_components/shared/video/video.types';
import { useLabel } from '@/app/_contexts/labels-context';
import { VideoSlide } from './video-slide';

interface VideoCarouselProps {
  items: VideoItem[];
  consentLabels?: VideoConsentLabels;
  imageSizes?: string;
  priority?: boolean;
  showNavigation?: boolean;
  showDots?: boolean;
  trackingPrefix?: string;
}

// Multi-video carousel. Extracted into its own module so embla is only loaded
// (via the dynamic import in video-carousel-client) when there is more than one
// video — single-video modules render a plain slide with no carousel JS.
export function VideoCarousel({
  items,
  consentLabels,
  imageSizes,
  priority = false,
  showNavigation = true,
  showDots = true,
  trackingPrefix,
}: VideoCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  const carouselLabel = useLabel('carousel.video', 'Video');

  // Track active slide
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const handlePlay = useCallback(() => {
    setHasInteracted(true);
  }, []);

  return (
    <Carousel
      opts={{ loop: true, align: 'start', watchDrag: true }}
      setApi={setApi}
      aria-label={carouselLabel}
    >
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem key={item.id} basis="full">
            <VideoSlide
              item={item}
              isActive={index === activeIndex}
              autoPlay={index === activeIndex && hasInteracted}
              imageSizes={imageSizes}
              priority={priority && index === 0}
              consentLabels={consentLabels}
              onPlay={handlePlay}
              trackingPrefix={trackingPrefix ? `${trackingPrefix}-slide-${getItemTrackId(item.id, index)}` : undefined}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      {(showNavigation || showDots) && (
        <CarouselControls className="mt-6">
          {showNavigation && <CarouselNavigation api={api} />}
          {showDots && <CarouselDots api={api} />}
        </CarouselControls>
      )}
    </Carousel>
  );
}
